import { memo, MouseEventHandler, useRef } from "react";
import { Box, Flex, IconButton, IconButtonProps, useDisclosure, useOutsideClick } from "@chakra-ui/react";
import { SkipNavLink } from "@chakra-ui/skip-nav";
import { Link } from "@mui/material";
import { useTranslation } from "next-i18next";
import { EnumNavigationTrack } from "src/analytics/enum";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { Button } from "src/components/base/button";
import { WfGlobalStyle } from "src/components/base/wf-global-style";
import { IconArrowRight, IconWikifolioShortLogo } from "src/components/icons";
import { IconSearch } from "src/components/icons-mui/search";
import { IconWikifolioLogo } from "src/components/icons-mui/wikifolio-logo";
import { GlobalNavSearch } from "src/components/layout/global-nav-search/global-nav-search";
import { Hamburger } from "src/components/layout/nav/hamburger";
import { MenuDesktop } from "src/components/layout/nav/menu-desktop";
import { MenuMobile } from "src/components/layout/nav/menu-mobile";
import { MenuUser } from "src/components/layout/nav/menu-user";
import { NewsTicker } from "src/components/layout/news-ticker/news-ticker";
import { HashLinkUnstyled } from "src/components-mui/base/hash-link/hash-link";
import { ID_NAV_MOBILE } from "src/constants/dom";
import { getGlobals } from "src/constants/globals";
import { LANGUAGE } from "src/constants/locale";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useUserStore } from "src/stores/user-store";
import { IUser } from "src/types/common";
import { INav } from "src/types/layout";
import { createMiddleClickHandler } from "src/utils/dom/mouse/mouse";
import { useRouterLocale } from "src/utils/router/use-router-locale";

const HIDE_MOBILE = ["none", "none", "flex"];
const SHOW_MOBILE = ["flex", "flex", "none"];

const SHORT_LOGO_LINK_SX = { mr: 2, px: 1, width: "auto" } as const;

interface IWikifolioLogoProps {
    logoUrl: string;
    onClick: () => void;
    onAuxClick?: MouseEventHandler<HTMLAnchorElement>;
}

const WikifolioLogo = ({ logoUrl, onClick, onAuxClick }: IWikifolioLogoProps) => {
    const { isLoggedIn } = useUserStore();
    return (
        <Link
            href={logoUrl}
            className="gtm-logo"
            aria-label="wikifolio Financial Technologies"
            title="wikifolio Financial Technologies"
            onClick={onClick}
            onAuxClick={onAuxClick}
            sx={{
                display: "block",
                flexShrink: 0,
                width: "133px",
                mr: 4,
                px: 0,
                ...(isLoggedIn ? SHORT_LOGO_LINK_SX : {}),
            }}
        >
            {isLoggedIn ? (
                <IconWikifolioShortLogo />
            ) : (
                <IconWikifolioLogo sx={{ width: "100%", height: "100%", overflow: "hidden", color: "green.600", verticalAlign: "top" }} />
            )}
        </Link>
    );
};

const SearchIconBtn = (props: IconButtonProps) => (
    <IconButton
        variant="ghost"
        colorScheme="gray"
        mr={[1, 2]}
        boxSize={[6, 6, 5]}
        minW={[6, 6, 5]}
        icon={<IconSearch sx={{ width: "24px", height: "24px" }} />}
        {...props}
    />
);

interface IProps {
    nav: INav;
    user: IUser | null;
    height: number[];
}

const NavComponent = ({ nav, user, height }: IProps) => {
    const { isOpen: isMobileMenuOpen, onToggle: onMobileMenuToggle, onClose: onMobileMenuClose } = useDisclosure();
    const { isOpen: isSearchOpen, onToggle: onSearchToggle, onClose: onSearchClose } = useDisclosure();
    const { buildLoginModalLink, buildRegisterModalLink } = useLinkBuilder();
    const genericActivity = useGenericActivityFromElysiumMutation();
    const { t } = useTranslation("common");
    const { language } = useRouterLocale();
    const navSearchRef = useRef<HTMLDivElement>(null);
    const { isNewsTickerHidden } = getGlobals();

    const isTickerEnabled = language === LANGUAGE.DE && !isNewsTickerHidden;

    const onHamburgerClick = () => {
        onMobileMenuToggle();

        if (!isMobileMenuOpen) {
            void genericActivity({
                activityTypeId: EnumActivityType.NavigationClicked,
                parameter: JSON.stringify({ type: EnumNavigationTrack.Burger }),
                referrer: window.location.href,
            });
        }
    };

    const onWikifolioLogoClick = (url: string) => {
        void genericActivity({
            activityTypeId: EnumActivityType.NavigationClicked,
            parameter: JSON.stringify({ type: EnumNavigationTrack.Logo, targetUrl: `${window.location.origin}${url}` }),
            referrer: window.location.href,
        });
    };

    const onSearchIconBtnClick = () => {
        onSearchToggle();

        if (!isSearchOpen) {
            void genericActivity({
                activityTypeId: EnumActivityType.NavigationClicked,
                parameter: JSON.stringify({ type: EnumNavigationTrack.Search }),
                referrer: window.location.href,
            });
        }
    };

    useOutsideClick({
        ref: navSearchRef,
        handler: onSearchClose,
    });

    return (
        <>
            {isTickerEnabled && <NewsTicker />}
            {isMobileMenuOpen && (
                <WfGlobalStyle
                    sx={{
                        body: {
                            overflow: ["hidden", "auto"],
                        },
                    }}
                />
            )}
            <SkipNavLink>
                {t("skip-to-content")} <IconArrowRight />
            </SkipNavLink>
            <Box
                as="header"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                position="fixed"
                top={isTickerEnabled ? 4 : 0}
                h={height}
                sx={{ transition: "height 0.2s" }}
                w="100%"
                bg="white"
                px={[3, 5, 5, 4]}
                pr={[0.5, 3, null]}
                boxShadow="xs"
                zIndex="sticky"
            >
                <WikifolioLogo
                    logoUrl={nav.logoUrl}
                    onClick={() => onWikifolioLogoClick(nav.logoUrl)}
                    onAuxClick={createMiddleClickHandler(() => onWikifolioLogoClick(nav.logoUrl))}
                />
                <MenuDesktop sx={{ display: HIDE_MOBILE }} nav={nav} isSearchOpen={isSearchOpen} />
                <Box flexGrow={1} />
                <SearchIconBtn
                    className="gtm-header-action__search"
                    aria-label={isSearchOpen ? t("close-search") : t("open-search")}
                    onClick={onSearchIconBtnClick}
                />
                <Hamburger
                    className="gtm-header-action__burger"
                    sx={{ display: SHOW_MOBILE }}
                    isOpen={isMobileMenuOpen}
                    aria-controls={ID_NAV_MOBILE}
                    aria-expanded={isMobileMenuOpen}
                    aria-label={isMobileMenuOpen ? t("close-nav") : t("open-nav")}
                    onClick={onHamburgerClick}
                />
                <Flex display={HIDE_MOBILE}>
                    {user ? (
                        <MenuUser user={user} links={nav.user!} h={height} />
                    ) : (
                        <>
                            <Button className="gtm-nav-menu__login" as={HashLinkUnstyled} href={buildLoginModalLink()} size="sm" variant="ghost">
                                {t("login-button")}
                            </Button>
                            <Button as={HashLinkUnstyled} href={buildRegisterModalLink()} size="sm" colorScheme="green" ml={2}>
                                {t("register")}
                            </Button>
                        </>
                    )}
                </Flex>
                <MenuMobile
                    isOpen={isMobileMenuOpen}
                    display={SHOW_MOBILE}
                    top={isTickerEnabled ? 11 : height}
                    nav={nav}
                    user={user}
                    onClose={onMobileMenuClose}
                />
                <GlobalNavSearch
                    ref={navSearchRef}
                    isOpen={isSearchOpen}
                    navHeights={height}
                    onSearchClose={onSearchClose}
                    sx={{ top: isTickerEnabled ? [0, 0, "32px"] : 0 }}
                />
            </Box>
        </>
    );
};

export const Nav = memo(NavComponent);
